import { useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { BrainCircuit } from "lucide-react";
import { api } from "@/lib/api";
import { SPORT_META } from "@/lib/odds";

const tooltipStyle = { background: "#121824", border: "1px solid #26334D", borderRadius: 8, fontSize: 12 };
const signed = (v, s = "") => `${v >= 0 ? "+" : ""}${v}${s}`;

export function ModelAnalytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/analytics/model").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="glass-card h-40 animate-pulse" />;
  const o = d.overall;
  return (
    <div className="glass-card p-6" data-testid="model-analytics">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><BrainCircuit size={20} className="text-cyan-400" /> Model analytics · last {d.window_days} days</h2>
          <p className="text-xs text-slate-500">How the AI's straight picks have actually performed. Flat ROI = betting 1 unit on every pick.</p>
        </div>
        <div className="flex gap-5">
          <Mini id="model-hit-rate" label="Hit rate" value={`${o.hit_rate}%`} sub={`${o.won}/${o.n} picks`} color="text-emerald-400" />
          <Mini id="model-calibration" label="Predicted" value={`${o.predicted}%`} sub={`gap ${signed((o.hit_rate - o.predicted).toFixed(1), "pt")}`} color="text-cyan-300" />
          <Mini id="model-flat-roi" label="Flat ROI" value={signed(o.flat_roi, "%")} sub={`${signed(o.units)}u`} color={o.flat_roi >= 0 ? "text-emerald-400" : "text-red-400"} />
        </div>
      </div>
      <div className="mt-5 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="h-56" data-testid="model-curve">
          <ResponsiveContainer>
            <AreaChart data={d.curve}>
              <defs><linearGradient id="mu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#06B6D4" stopOpacity={0.4} /><stop offset="100%" stopColor="#06B6D4" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#26334D" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={11} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#64748B" fontSize={11} tickFormatter={(v) => `${v}u`} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}u`, "Cumulative units"]} />
              <Area type="monotone" dataKey="units" stroke="#06B6D4" strokeWidth={2} fill="url(#mu)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1.5 text-sm" data-testid="model-by-sport">
          <p className="label-caps mb-2">By sport · hit rate / flat ROI</p>
          {d.by_sport.map((s) => (
            <div key={s.sport} className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: SPORT_META[s.sport]?.color }} />
              <span className="w-32 truncate text-slate-300">{s.label}</span>
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#26334D]"><div className="h-full rounded-full bg-emerald-400" style={{ width: `${s.hit_rate}%` }} /></div>
              <span className="font-mono-num w-12 text-right text-slate-200">{s.hit_rate}%</span>
              <span className={`font-mono-num w-16 text-right ${s.flat_roi >= 0 ? "text-emerald-400" : "text-red-400"}`}>{signed(s.flat_roi, "%")}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2" data-testid="model-by-market">
        <div><p className="label-caps mb-2">By market</p>{d.by_market.map((m) => <Row key={m.market} label={m.market} a={m} />)}</div>
        <div><p className="label-caps mb-2">By confidence</p>{d.by_confidence.map((m) => <Row key={m.confidence} label={m.confidence} a={m} />)}</div>
      </div>
    </div>
  );
}

function Row({ label, a }) {
  return <div className="flex items-center justify-between border-b border-[#26334D]/60 py-1 text-sm"><span className="capitalize text-slate-300">{label} <span className="text-xs text-slate-600">({a.n})</span></span><span className="font-mono-num text-slate-200">{a.hit_rate}% <span className={a.flat_roi >= 0 ? "text-emerald-400" : "text-red-400"}>{signed(a.flat_roi, "%")}</span></span></div>;
}

function Mini({ id, label, value, sub, color }) {
  return <div className="text-right"><p className="label-caps">{label}</p><p data-testid={id} className={`font-mono-num text-2xl font-bold ${color}`}>{value}</p><p className="text-[11px] text-slate-500">{sub}</p></div>;
}
